import React, { PropsWithChildren, ReactNode, useCallback } from "react";

import "./styles.scss";

/** "default" is our default label styling, currently slightly smaller than regular text. "paragraph" is styled like regular text, making it more suitable to question-style labels. */
type VisibleLabelVariant = "default" | "paragraph";
/** "default" is our default label styling, currently slightly smaller than regular text. "paragraph" is styled like regular text, making it more suitable to question-style labels. "hidden" visually hides the label and only shows it to screen-readers. */
export type LabelVariant = VisibleLabelVariant | "hidden";

export default function InputLabel({
  children,
  variant = "default",
  htmlFor,
  className,
}: PropsWithChildren<{
  variant?: LabelVariant;
  /** This is optional — you can wrap the input in the label. */
  htmlFor?: string;
  className?: string;
}>) {
  return (
    <label className={getClassName(variant, className)} htmlFor={htmlFor}>
      {children}
    </label>
  );
}

/** This is for contenteditable divs — it might work with regular inputs but don't use it for them (see above). Currently this is only used in the inline lexical editor. */
export function ContentEditableLabel({
  children,
  variant = "default",
  htmlFor,
  className,
  id,
}: PropsWithChildren<{
  variant?: VisibleLabelVariant;
  htmlFor: string;
  className?: string;
  /** For use with aria-labelled-by on an input component */
  id: string;
}>) {
  const onClickHandler = useCallback(() => {
    if (!htmlFor) return;
    const labelEl = document.getElementById(htmlFor!);
    labelEl?.focus();
  }, [htmlFor]);

  return (
    <span onClick={onClickHandler} id={id} className={getClassName(variant, className)}>
      {children}
    </span>
  );
}

type Level = 1 | 2 | 3 | 4 | 5 | 6;
/** A heading styled to look like a label (or just a div) */
export function LabelHeading({
  className,
  level,
  badges,
  children,
}: PropsWithChildren<{ className?: string; level?: Level; badges?: ReactNode }>) {
  const Tag: `h${Level}` | "div" = level ? `h${level}` : "div";
  return (
    <Tag className={`c-input-label ${className}`}>
      <span className="c-input-label__main">{children}</span>
      {badges}
    </Tag>
  );
}

const Classes: Record<LabelVariant, string> = {
  default: "c-input-label--default",
  paragraph: "c-input-label--paragraph",
  hidden: "is-sr-only",
};

function getClassName(variant: LabelVariant, className?: string) {
  return `c-input-label ${Classes[variant]} ${className ?? ""}`;
}
